import React, { useState, useEffect, useCallback } from 'react';
import { 
  Shield, RefreshCw, AlertCircle, Filter, Lock, Unlock,
  AlertTriangle, CheckCircle, XCircle, Globe, User, Clock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { toast } from 'sonner';
import api from '@/lib/api';

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
};

// Calculate time ago
const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
};

// Summary Card Component
const SummaryCard = ({ title, value, subtitle, icon: Icon, trend, trendDirection, color = 'primary' }) => {
  const colorClasses = {
    primary: 'text-primary',
    success: 'text-green-500',
    warning: 'text-yellow-500',
    danger: 'text-red-500',
    muted: 'text-muted-foreground'
  };
  
  return (
    <Card className="border-border bg-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className={`text-3xl font-bold ${colorClasses[color]}`}>{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
          <div className={`rounded-full p-3 bg-secondary ${colorClasses[color]}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
        {trend && (
          <div className={`mt-3 text-xs ${trendDirection === 'up' ? 'text-red-400' : 'text-green-400'}`}>
            {trend}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Suspicious IP Row Component
const SuspiciousIPRow = ({ ip, attempts, lastAttempt, onUnblock }) => (
  <div className="flex items-center justify-between p-3 rounded-lg bg-red-500/10 border border-red-500/20">
    <div className="flex items-center gap-3">
      <div className="p-2 rounded-full bg-red-500/20">
        <AlertTriangle className="h-4 w-4 text-red-400" />
      </div>
      <div>
        <p className="font-mono font-medium text-red-400">{ip}</p>
        <p className="text-xs text-muted-foreground">{attempts} failed attempts • {timeAgo(lastAttempt)}</p>
      </div>
    </div>
    <Button 
      variant="outline" 
      size="sm" 
      onClick={() => onUnblock(ip)}
      className="border-red-500/30 text-red-400 hover:bg-red-500/20"
      data-testid={`unblock-ip-${ip}`}
    >
      <Unlock className="mr-2 h-4 w-4" />
      Unblock
    </Button>
  </div>
);

export function Security() {
  const [attempts, setAttempts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [timeRange, setTimeRange] = useState('24h');
  const [filterUsername, setFilterUsername] = useState('');
  const [filterIP, setFilterIP] = useState('');
  const [filterSuccess, setFilterSuccess] = useState('all');
  
  // Unblock dialog
  const [unblockTarget, setUnblockTarget] = useState(null);
  const [isUnblocking, setIsUnblocking] = useState(false);
  
  // Chart data state
  const [chartData, setChartData] = useState([]);

  // Fetch login attempts
  const fetchLoginAttempts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = {
        range: timeRange,
        limit: 200
      };
      if (filterUsername) params.username = filterUsername;
      if (filterIP) params.ip = filterIP;
      if (filterSuccess !== 'all') params.success = filterSuccess === 'true';
      
      const data = await api.getLoginAttempts(params);
      setAttempts(data.attempts || []);
      setSummary(data.summary || {});
      
      // Process chart data
      processChartData(data.attempts || []);
    } catch (err) {
      setError(err.message || 'Failed to load login attempts');
      toast.error('Failed to load security data');
    } finally {
      setIsLoading(false);
    }
  }, [timeRange, filterUsername, filterIP, filterSuccess]);

  // Process attempts into chart data
  const processChartData = (attemptsList) => {
    if (!attemptsList.length) {
      setChartData([]);
      return;
    }
    
    // Group by hour for 24h/7d, by day for 30d
    const groupByHour = timeRange === '1h' || timeRange === '24h';
    const grouping = {};
    
    attemptsList.forEach(attempt => {
      const date = new Date(attempt.created_at);
      let key;
      if (groupByHour) {
        key = date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric' });
      } else {
        key = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
      
      if (!grouping[key]) {
        grouping[key] = { name: key, success: 0, failed: 0 };
      }
      if (attempt.success) {
        grouping[key].success++;
      } else {
        grouping[key].failed++;
      }
    });
    
    // Sort and convert to array
    const sorted = Object.values(grouping).reverse();
    setChartData(sorted);
  };

  // Calculate suspicious IPs (5+ failed attempts)
  const getSuspiciousIPs = () => {
    const ipCounts = {};
    attempts
      .filter(a => !a.success)
      .forEach(a => {
        if (!ipCounts[a.ip]) {
          ipCounts[a.ip] = { count: 0, lastAttempt: a.created_at };
        }
        ipCounts[a.ip].count++;
        if (new Date(a.created_at) > new Date(ipCounts[a.ip].lastAttempt)) {
          ipCounts[a.ip].lastAttempt = a.created_at;
        }
      });
    
    return Object.entries(ipCounts)
      .filter(([_, data]) => data.count >= 3)
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 5)
      .map(([ip, data]) => ({ ip, attempts: data.count, lastAttempt: data.lastAttempt }));
  };

  // Handle unblock
  const handleUnblock = async () => {
    if (!unblockTarget) return;
    
    setIsUnblocking(true);
    try {
      await api.unblockLoginAttempts({ ip: unblockTarget });
      toast.success(`Unblocked IP: ${unblockTarget}`);
      setUnblockTarget(null);
      fetchLoginAttempts();
    } catch (err) {
      toast.error(err.message || 'Failed to unblock IP');
    } finally {
      setIsUnblocking(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchLoginAttempts();
  }, [fetchLoginAttempts]);

  // Debounced filter search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchLoginAttempts();
    }, 500);
    return () => clearTimeout(timer);
  }, [filterUsername, filterIP]);

  const suspiciousIPs = getSuspiciousIPs();
  const successRate = summary?.total_success && summary?.total_failed 
    ? Math.round((summary.total_success / (summary.total_success + summary.total_failed)) * 100)
    : 0;

  if (error && attempts.length === 0) {
    return (
      <div className="space-y-6" data-testid="security-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Security</h1>
            <p className="text-muted-foreground">Monitor login attempts and security events</p>
          </div>
        </div>
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="flex items-center gap-4 p-6">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <div className="flex-1">
              <p className="font-medium">Failed to load security data</p>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
            <Button variant="outline" onClick={fetchLoginAttempts}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="security-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Security</h1>
          <p className="text-muted-foreground">Monitor login attempts and security events</p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchLoginAttempts}
          disabled={isLoading}
          data-testid="refresh-security-btn"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <SummaryCard 
          title="Total Attempts"
          value={summary ? (summary.total_success + summary.total_failed).toLocaleString() : '-'}
          subtitle={`in last ${timeRange}`}
          icon={Shield}
          color="primary"
        />
        <SummaryCard 
          title="Failed Attempts"
          value={summary?.total_failed?.toLocaleString() || '0'}
          subtitle="blocked or failed"
          icon={XCircle}
          color="danger"
        />
        <SummaryCard 
          title="Success Rate"
          value={`${successRate}%`}
          subtitle="successful logins"
          icon={CheckCircle}
          color={successRate > 80 ? 'success' : successRate > 50 ? 'warning' : 'danger'}
        />
        <SummaryCard 
          title="Unique IPs"
          value={summary?.unique_ips?.toLocaleString() || '0'}
          subtitle="source addresses"
          icon={Globe}
          color="muted"
        />
        <SummaryCard 
          title="Unique Users"
          value={summary?.unique_users?.toLocaleString() || '0'}
          subtitle="attempted usernames"
          icon={User}
          color="muted"
        />
      </div>

      {/* Suspicious IPs Alert */}
      {suspiciousIPs.length > 0 && (
        <Card className="border-red-500/30 bg-red-500/5">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="h-5 w-5" />
              Suspicious Activity Detected
              <Badge variant="destructive" className="ml-2">{suspiciousIPs.length} IPs</Badge>
            </CardTitle>
            <CardDescription>
              These IP addresses have multiple failed login attempts
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {suspiciousIPs.map(({ ip, attempts, lastAttempt }) => (
              <SuspiciousIPRow 
                key={ip} 
                ip={ip} 
                attempts={attempts} 
                lastAttempt={lastAttempt}
                onUnblock={setUnblockTarget}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Login Attempts Chart */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            Login Attempts Over Time
          </CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                  tickLine={{ stroke: 'hsl(var(--border))' }}
                />
                <YAxis 
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                  tickLine={{ stroke: 'hsl(var(--border))' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="success" 
                  stroke="#22c55e" 
                  strokeWidth={2}
                  dot={false}
                  name="Successful"
                />
                <Line 
                  type="monotone" 
                  dataKey="failed" 
                  stroke="#ef4444" 
                  strokeWidth={2}
                  dot={false}
                  name="Failed"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[250px] text-muted-foreground">
              No data available for chart
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="border-border bg-card">
        <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Filters:</span>
          </div>
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32" data-testid="filter-time-range">
              <SelectValue placeholder="Time Range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1h">Last Hour</SelectItem>
              <SelectItem value="24h">Last 24h</SelectItem>
              <SelectItem value="7d">Last 7 Days</SelectItem>
              <SelectItem value="30d">Last 30 Days</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterSuccess} onValueChange={setFilterSuccess}>
            <SelectTrigger className="w-36" data-testid="filter-success">
              <SelectValue placeholder="All Results" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Results</SelectItem>
              <SelectItem value="true">Successful Only</SelectItem>
              <SelectItem value="false">Failed Only</SelectItem>
            </SelectContent>
          </Select>
          <Input
            placeholder="Filter by username..."
            value={filterUsername}
            onChange={(e) => setFilterUsername(e.target.value)}
            className="w-44"
            data-testid="filter-username-input"
          />
          <Input
            placeholder="Filter by IP..."
            value={filterIP}
            onChange={(e) => setFilterIP(e.target.value)}
            className="w-40"
            data-testid="filter-ip-input"
          />
        </CardContent>
      </Card>

      {/* Attempts Table */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-primary" />
            Login Attempts
            <Badge variant="secondary" className="ml-2">
              {attempts.length} entries
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="h-12 animate-pulse rounded bg-muted" />
              ))}
            </div>
          ) : attempts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Shield className="h-12 w-12 text-muted-foreground/30 mb-4" />
              <p className="text-lg font-medium">No login attempts found</p>
              <p className="text-sm text-muted-foreground">
                Try adjusting your filters or time range
              </p>
            </div>
          ) : (
            <div className="rounded-md border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-[100px]">Status</TableHead>
                    <TableHead>Username</TableHead>
                    <TableHead>IP Address</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {attempts.map((attempt, i) => (
                    <TableRow 
                      key={attempt._id || i}
                      className={!attempt.success ? 'bg-red-500/5' : ''}
                    >
                      <TableCell>
                        {attempt.success ? (
                          <Badge className="bg-green-500/20 text-green-400">
                            <CheckCircle className="mr-1 h-3 w-3" />
                            Success
                          </Badge>
                        ) : (
                          <Badge className="bg-red-500/20 text-red-400">
                            <XCircle className="mr-1 h-3 w-3" />
                            Failed
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{attempt.username || 'N/A'}</TableCell>
                      <TableCell className="font-mono text-sm">{attempt.ip || 'N/A'}</TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-sm">{formatDate(attempt.created_at)}</span>
                          <span className="text-xs text-muted-foreground">{timeAgo(attempt.created_at)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {!attempt.success && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setUnblockTarget(attempt.ip)}
                            data-testid={`unblock-${attempt._id || i}`}
                          >
                            <Unlock className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unblock Confirmation Dialog */}
      <AlertDialog open={!!unblockTarget} onOpenChange={() => setUnblockTarget(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Unblock IP Address?</AlertDialogTitle>
            <AlertDialogDescription>
              This will clear the rate limit for IP address <code className="text-primary">{unblockTarget}</code>. 
              They will be able to attempt login again immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleUnblock}
              disabled={isUnblocking}
              className="bg-primary"
            >
              {isUnblocking ? 'Unblocking...' : 'Unblock IP'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default Security;
